import { SortOrderType } from '@repo/common';

import { UrlSearchFilterProperty } from '@/constants';
import { useGetAllPurchasesQuery } from '@/features/slice/purchases';
import { useUrlFilter, useUrlPagination } from '@/hooks';
import { TQueryFilterParams } from '@/types';

import { mapDateObjectToString } from '../../../utils/mappers/map-date-object-to-string.mapper';

type UsePurchaseListProps = {
    supplierId?: string;
    purchaseType?: string;
};

export const usePurchaseList = ({
    supplierId: sid,
    purchaseType,
}: UsePurchaseListProps = {}) => {
    const { debouncedSearchText, sortBy, sortOrder, getSearchFilter } =
        useUrlFilter({
            defaultSortBy: 'created_at',
            defaultSortOrder: SortOrderType.DESC,
        });
    const { limit, page } = useUrlPagination();

    const paymentStatus = getSearchFilter(
        UrlSearchFilterProperty.PAYMENT_STATUS,
    );

    const supplierId =
        sid ?? getSearchFilter(UrlSearchFilterProperty.SUPPLIER_ID);

    const userId = getSearchFilter(UrlSearchFilterProperty.USER_ID);

    const fromDate =
        getSearchFilter(UrlSearchFilterProperty.DATE_FROM) || undefined;
    const toDate =
        getSearchFilter(UrlSearchFilterProperty.DATE_TO) || undefined;

    const queryFilters: TQueryFilterParams = {
        limit,
        page,
        search: debouncedSearchText,
        sort_by: sortBy,
        sort_order: sortOrder,
    };

    if (paymentStatus) {
        queryFilters['status'] = paymentStatus;
    }

    if (supplierId) {
        queryFilters['supplier_id'] = supplierId;
    }

    if (userId) {
        queryFilters['user_id'] = userId;
    }

    if (fromDate) {
        const dateObj = new Date(fromDate);
        queryFilters['start_date'] = mapDateObjectToString(dateObj);
    }
    if (toDate) {
        const dateObj = new Date(toDate);
        queryFilters['end_date'] = mapDateObjectToString(dateObj);
    }

    if (purchaseType) {
        queryFilters['purchase_type'] = purchaseType;
    }

    const { data, isLoading, isError, error, isFetching } =
        useGetAllPurchasesQuery(queryFilters);

    return {
        data,
        isLoading,
        isFetching,
        isError,
        error,
    };
};
